import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Stethoscope, User, MapPin, FileCheck, Phone, Building2,
  ArrowLeft, Loader2, CheckCircle2, Upload, X, AlertCircle,
  ExternalLink, Plus, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Especialidades médicas
const SPECIALTIES = [
  "Medicina General",
  "Medicina Interna",
  "Pediatría",
  "Ginecología y Obstetricia",
  "Cardiología",
  "Dermatología",
  "Endocrinología",
  "Gastroenterología",
  "Geriatría",
  "Hematología",
  "Infectología",
  "Nefrología",
  "Neumología",
  "Neurología",
  "Nutrición",
  "Oftalmología",
  "Oncología",
  "Otorrinolaringología",
  "Psiquiatría",
  "Reumatología",
  "Traumatología y Ortopedia",
  "Urología",
  "Otra especialidad"
];

// Países de LATAM
const COUNTRIES = [
  { code: "AR", name: "Argentina", provinces: ["Buenos Aires", "CABA", "Córdoba", "Santa Fe", "Mendoza", "Tucumán", "Entre Ríos", "Salta", "Misiones", "Chaco", "Corrientes", "Santiago del Estero", "San Juan", "Jujuy", "Río Negro", "Neuquén", "Formosa", "Chubut", "San Luis", "Catamarca", "La Rioja", "La Pampa", "Santa Cruz", "Tierra del Fuego"] },
  { code: "CL", name: "Chile", provinces: ["Región Metropolitana", "Valparaíso", "Biobío", "Maule", "La Araucanía", "O'Higgins", "Los Lagos", "Coquimbo", "Antofagasta", "Los Ríos"] },
  { code: "CO", name: "Colombia", provinces: ["Bogotá D.C.", "Antioquia", "Valle del Cauca", "Cundinamarca", "Atlántico", "Santander", "Bolívar", "Nariño", "Córdoba", "Tolima"] },
  { code: "MX", name: "México", provinces: ["CDMX", "Estado de México", "Jalisco", "Nuevo León", "Puebla", "Guanajuato", "Chihuahua", "Veracruz", "Baja California", "Querétaro"] },
  { code: "PE", name: "Perú", provinces: ["Lima", "Arequipa", "La Libertad", "Piura", "Cusco", "Lambayeque", "Junín", "Áncash", "Cajamarca", "Ica"] },
  { code: "UY", name: "Uruguay", provinces: ["Montevideo", "Canelones", "Maldonado", "Salto", "Colonia", "Paysandú", "Rivera", "Tacuarembó", "Cerro Largo", "Rocha"] },
  { code: "EC", name: "Ecuador", provinces: ["Pichincha", "Guayas", "Azuay", "Manabí", "El Oro", "Tungurahua", "Loja", "Esmeraldas", "Imbabura", "Chimborazo"] },
  { code: "OTHER", name: "Otro país", provinces: [] }
];

const DoctorVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [step, setStep] = useState('verifying'); // verifying | profile | submitted | error
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    sex: "",
    dni: "",
    cuil: "",
    specialty: "",
    country: "Argentina", // 90%+ of expected users; can still be changed
    province: "",
    license_number: "",
    license_issuer: "",
    matriculas: [{ number: "", issuer: "" }],
    phone: ""
  });
  
  const [credentialFile, setCredentialFile] = useState(null);
  const [uploadingCredential, setUploadingCredential] = useState(false);

  // Verify email token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStep('error');
        setErrorMessage("Token no proporcionado");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/doctor/verify?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          // Store JWT token
          localStorage.setItem('auth_token', data.token);
          setStep('profile');
          toast.success("Email verificado. Completa tu perfil.");
        } else {
          setStep('error');
          setErrorMessage(data.detail || "Token inválido o expirado");
        }
      } catch (error) {
        console.error('Error:', error);
        setStep('error');
        setErrorMessage("Error de conexión");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    
    // Reset province when country changes
    if (field === 'country') {
      setProfileData(prev => ({ ...prev, province: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Formato no permitido. Usa JPG, PNG, WebP o PDF");
        return;
      }
      // Validate size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Archivo muy grande. Máximo 5MB");
        return;
      }
      setCredentialFile(file);
    }
  };

  const uploadCredential = async () => {
    if (!credentialFile) return null;

    setUploadingCredential(true);
    try {
      const formData = new FormData();
      formData.append('file', credentialFile);

      const response = await fetch(`${API_URL}/api/auth/doctor/upload-credential`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.credential_url;
      } else {
        toast.error("Error al subir documento");
        return null;
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error al subir documento");
      return null;
    } finally {
      setUploadingCredential(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty matrículas (user could add a row and leave it blank).
    const cleanedMatriculas = (profileData.matriculas || [])
      .map(m => ({ number: (m.number || "").trim(), issuer: (m.issuer || "").trim() }))
      .filter(m => m.number && m.issuer);

    if (cleanedMatriculas.length === 0) {
      toast.error("Cargá al menos una matrícula profesional");
      return;
    }

    // Validate required fields. `dni` and `sex` are only required when country
    // is Argentina (matches how these IDs work — a Chilean doctor won't have
    // an Argentine DNI). CUIL follows the same rule.
    const isArg = profileData.country === "Argentina";
    const required = ['first_name', 'last_name', 'specialty', 'country'];
    if (isArg) required.push('dni', 'sex', 'cuil');
    const missing = required.filter(field => !profileData[field]);

    if (missing.length > 0) {
      toast.error("Completá todos los campos obligatorios");
      return;
    }

    // Basic DNI/CUIL format sanity — 7-8 digits for DNI, 11 for CUIL (with or
    // without dashes/spaces which we normalize before validating).
    if (isArg) {
      const dniDigits = profileData.dni.replace(/\D/g, '');
      if (dniDigits.length < 7 || dniDigits.length > 8) {
        toast.error("El DNI debe tener 7 u 8 dígitos");
        return;
      }
      const cuilDigits = profileData.cuil.replace(/\D/g, '');
      if (cuilDigits.length !== 11) {
        toast.error("El CUIL debe tener 11 dígitos");
        return;
      }
    }

    setSubmitting(true);
    try {
      // Upload credential first if present
      if (credentialFile) {
        await uploadCredential();
      }

      // Mirror the primary matrícula into the legacy fields so the request
      // stays compatible with older backends during the rollout window.
      const primary = cleanedMatriculas[0];
      const payload = {
        ...profileData,
        matriculas: cleanedMatriculas,
        license_number: primary.number,
        license_issuer: primary.issuer,
      };

      // Submit profile
      const response = await fetch(`${API_URL}/api/auth/doctor/complete-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        setStep('submitted');
        toast.success("Perfil enviado para revisión");
      } else {
        toast.error(data.detail || "Error al enviar perfil");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error de conexión");
    } finally {
      setSubmitting(false);
    }
  };

  const updateMatricula = (index, field, value) => {
    setProfileData(prev => {
      const next = [...(prev.matriculas || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, matriculas: next };
    });
  };

  const addMatricula = () => {
    setProfileData(prev => ({
      ...prev,
      matriculas: [...(prev.matriculas || []), { number: "", issuer: "" }]
    }));
  };

  const removeMatricula = (index) => {
    setProfileData(prev => {
      const next = (prev.matriculas || []).filter((_, i) => i !== index);
      // Always keep at least one row so the form doesn't collapse
      return { ...prev, matriculas: next.length ? next : [{ number: "", issuer: "" }] };
    });
  };

  const selectedCountry = COUNTRIES.find(c => c.name === profileData.country);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Verificando email...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Error de verificación</h2>
          <p className="text-slate-600 mb-6">{errorMessage}</p>
          <Button onClick={() => navigate('/doctor/register')}>
            Volver a intentar
          </Button>
        </div>
      </div>
    );
  }

  // Submitted state
  if (step === 'submitted') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Solicitud enviada!</h2>
          <p className="text-slate-600 mb-4">
            Tu perfil profesional está siendo revisado por nuestro equipo.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-6">
            <p className="text-sm text-blue-800">
              <strong>¿Qué sigue?</strong>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1">
              <li>• Verificaremos tu matrícula profesional</li>
              <li>• Recibirás un email cuando tu cuenta sea aprobada</li>
              <li>• El proceso normalmente toma 1-2 días hábiles</li>
            </ul>
          </div>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  // Profile form
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#1d366b] text-white p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                <Stethoscope className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Completa tu Perfil Profesional</h1>
                <p className="text-slate-300 text-sm">Paso 2 de 2 - Datos profesionales</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <User className="h-4 w-4" />
                Datos Personales
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Nombre *</Label>
                  <Input
                    id="first_name"
                    value={profileData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="Juan"
                    required
                    data-testid="doctor-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Apellido *</Label>
                  <Input
                    id="last_name"
                    value={profileData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Pérez"
                    required
                    data-testid="doctor-last-name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+54 11 1234-5678"
                    className="pl-10"
                    data-testid="doctor-phone"
                  />
                </div>
              </div>

              {/* Identity fields — required for Argentine doctors so admin can
                  verify the professional against public sources (DNI + SISA). */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="dni">DNI {profileData.country === 'Argentina' && '*'}</Label>
                  <Input
                    id="dni"
                    value={profileData.dni}
                    onChange={(e) => handleInputChange('dni', e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="31565386"
                    data-testid="doctor-dni"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <Label htmlFor="cuil">CUIL {profileData.country === 'Argentina' && '*'}</Label>
                  <Input
                    id="cuil"
                    value={profileData.cuil}
                    onChange={(e) => handleInputChange('cuil', e.target.value.replace(/[^\d-]/g, '').slice(0, 13))}
                    placeholder="20-31565386-0"
                    data-testid="doctor-cuil"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <Label htmlFor="sex">Sexo {profileData.country === 'Argentina' && '*'}</Label>
                  <Select
                    value={profileData.sex}
                    onValueChange={(value) => handleInputChange('sex', value)}
                  >
                    <SelectTrigger data-testid="doctor-sex">
                      <SelectValue placeholder="Seleccioná" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                      <SelectItem value="X">X</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Professional Info */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <FileCheck className="h-4 w-4" />
                Datos Profesionales
              </h3>

              <div>
                <Label>Especialidad *</Label>
                <Select
                  value={profileData.specialty}
                  onValueChange={(value) => handleInputChange('specialty', value)}
                >
                  <SelectTrigger data-testid="doctor-specialty">
                    <SelectValue placeholder="Selecciona tu especialidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(spec => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>País *</Label>
                  <Select
                    value={profileData.country}
                    onValueChange={(value) => handleInputChange('country', value)}
                  >
                    <SelectTrigger data-testid="doctor-country">
                      <SelectValue placeholder="Selecciona país" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Provincia/Estado</Label>
                  <Select
                    value={profileData.province}
                    onValueChange={(value) => handleInputChange('province', value)}
                    disabled={!selectedCountry?.provinces?.length}
                  >
                    <SelectTrigger data-testid="doctor-province">
                      <SelectValue placeholder="Selecciona provincia" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedCountry?.provinces?.map(prov => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* License Info — multi-matrícula support so a doctor working in
                several provinces can register all their Colegios in one shot,
                just like MisRx. LSF is optional here (courier flow doesn't
                require it) but captured for future receta-integrated flows. */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Matrículas Profesionales
                </h3>
                <a
                  href="https://sisa.msal.gov.ar/sisa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  data-testid="doctor-sisa-link"
                >
                  Verificar en SISA <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              <p className="text-xs text-slate-500 -mt-2">
                Aceptamos matrícula <strong>provincial (MP)</strong> o <strong>nacional (MN)</strong>.
                Si tenés más de una, cargá cada una por separado
                (ej. <span className="whitespace-nowrap">MN 139048 — CABA</span>, <span className="whitespace-nowrap">MP 6155 — Santa Fe</span>).
              </p>

              {(profileData.matriculas || []).map((mat, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-3 items-end" data-testid={`matricula-row-${idx}`}>
                  <div>
                    <Label>Número de matrícula {idx === 0 && '*'}</Label>
                    <Input
                      value={mat.number}
                      onChange={(e) => updateMatricula(idx, 'number', e.target.value)}
                      placeholder="Ej: 139048"
                      data-testid={`matricula-number-${idx}`}
                    />
                  </div>
                  <div>
                    <Label>Tipo y jurisdicción {idx === 0 && '*'}</Label>
                    <Input
                      value={mat.issuer}
                      onChange={(e) => updateMatricula(idx, 'issuer', e.target.value)}
                      placeholder="Ej: MP - Santa Fe  /  MN - Nacional"
                      data-testid={`matricula-issuer-${idx}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMatricula(idx)}
                    disabled={(profileData.matriculas || []).length <= 1}
                    aria-label="Quitar matrícula"
                    className="text-slate-400 hover:text-red-600 h-10 w-10 p-0"
                    data-testid={`matricula-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addMatricula}
                className="w-full sm:w-auto"
                data-testid="matricula-add-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar otra matrícula
              </Button>

              {/* Credential Upload */}
              <div>
                <Label>Documento de Credencial (opcional)</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Sube una foto o scan de tu matrícula para agilizar la verificación
                </p>
                {credentialFile ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <FileCheck className="h-5 w-5 text-green-600" />
                    <span className="text-sm flex-1 truncate">{credentialFile.name}</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setCredentialFile(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="h-5 w-5 text-slate-400" />
                    <span className="text-sm text-slate-600">Subir archivo (JPG, PNG, PDF - máx. 5MB)</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="doctor-credential-upload"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="pt-6 border-t">
              <Button 
                type="submit" 
                className="w-full"
                disabled={submitting || uploadingCredential}
                data-testid="doctor-profile-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar solicitud de registro"
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center mt-3">
                Tu información será revisada por nuestro equipo. Te notificaremos cuando tu cuenta esté activa.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DoctorVerify;
